import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { createPatient, searchPatients } from '../../../services/api';
import type { Patient } from '../../../services/api';
import { useInbox } from '../../../whatsapp/InboxProvider';
import {
  CREATE_ERROR_TEXT,
  SEARCH_DEBOUNCE_MS,
  SEARCH_ERROR_TEXT,
  canCreate,
  createdPatientId,
  linkFailure,
  localPhone,
  searchTerm,
  visitLabel,
} from '../../../whatsapp/patientLink';
import type { Conversation } from '../../../types/whatsapp';
import { Modal } from '../Modal';

const secondaryButton =
  'inline-flex h-11 items-center justify-center rounded-full border border-stone-200 bg-white px-6 text-sm font-semibold text-stone-800 no-underline hover:bg-stone-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-60';
const primaryButton =
  'inline-flex h-11 items-center justify-center rounded-full bg-blue-600 px-6 text-sm font-semibold text-white hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:bg-stone-200 disabled:text-stone-600';
const fieldClass =
  'h-11 w-full rounded-xl border border-stone-400 bg-white px-3.5 text-sm text-stone-800 outline-none focus-visible:border-blue-600 focus-visible:ring-2 focus-visible:ring-blue-600/30 disabled:opacity-60';

interface ConversationProps {
  conversation: Conversation;
}

interface LinkDialogProps extends ConversationProps {
  onClose: () => void;
  onLinked: () => void;
  onCreate: () => void;
}

type SearchState = 'idle' | 'loading' | 'ready' | 'error';

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const WarningIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
    <path d="M10.3 3.9 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
  </svg>
);

/** "Vincular a un paciente": search the clinic's patients and link the chosen file to the conversation. */
export const LinkPatientDialog = ({ conversation, onClose, onLinked, onCreate }: LinkDialogProps) => {
  const { linkPatient } = useInbox();
  const ids = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const seqRef = useRef(0);

  const [raw, setRaw] = useState('');
  const [results, setResults] = useState<Patient[]>([]);
  const [searchState, setSearchState] = useState<SearchState>('idle');
  const [selected, setSelected] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const term = searchTerm(raw);

  useEffect(() => {
    if (!term.ready) {
      setResults([]);
      setSearchState('idle');
      return;
    }
    const seq = ++seqRef.current;
    setSearchState('loading');
    const timer = setTimeout(() => {
      searchPatients(term.query, 10)
        .then((found) => {
          if (seq !== seqRef.current) return;
          setResults(found ?? []);
          setSearchState('ready');
        })
        .catch(() => {
          if (seq === seqRef.current) setSearchState('error');
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      // Whatever this search still returns is stale.
      seqRef.current++;
    };
  }, [term.query, term.ready]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (selected === null || busy) return;
    setBusy(true);
    setError(null);
    try {
      await linkPatient(conversation.id, selected);
      onLinked();
    } catch (err) {
      const failure = linkFailure(err);
      setError(failure.text);
      if (failure.kind === 'gone') setSelected(null);
    } finally {
      setBusy(false);
    }
  };

  const titleId = `${ids}-t`;
  const descId = `${ids}-d`;

  return (
    <Modal
      role="dialog"
      titleId={titleId}
      descriptionId={descId}
      maxWidth="max-w-[480px]"
      locked={busy}
      onClose={onClose}
      initialFocus={inputRef}
    >
      <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <h2 id={titleId} className="text-xl font-bold text-stone-900">
            Vincular a un paciente
          </h2>
          <p id={descId} className="text-sm leading-[1.55] text-stone-600">
            Elige la ficha de quien escribe desde{' '}
            <span className="font-geist-mono text-stone-800">{conversation.display_phone}</span>.
          </p>
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold text-stone-800">Buscar paciente</span>
          <span className="flex h-11 items-center gap-2 rounded-full border border-stone-400 bg-white px-3.5 text-stone-600 focus-within:border-blue-600 focus-within:ring-2 focus-within:ring-blue-600/30">
            <SearchIcon />
            <input
              ref={inputRef}
              type="search"
              value={raw}
              autoComplete="off"
              placeholder="Nombre, teléfono, correo o NIT"
              onChange={(e) => {
                setRaw(e.target.value);
                setSelected(null);
                setError(null);
              }}
              className="min-w-0 flex-1 border-0 bg-transparent text-sm text-stone-800 outline-none"
            />
          </span>
        </label>

        <div aria-live="polite" className="flex min-h-[184px] flex-col gap-2">
          {!term.ready && (
            <p className="px-1 py-3 text-[13px] leading-[1.5] text-stone-600">
              Escribe al menos 3 letras o números para buscar. Puedes usar el nombre, el teléfono, el correo o el NIT.
            </p>
          )}
          {term.ready && searchState === 'loading' && (
            <p className="px-1 py-3 text-[13px] text-stone-600">Buscando…</p>
          )}
          {term.ready && searchState === 'error' && (
            <p role="alert" className="px-1 py-3 text-[13px] text-red-700">
              {SEARCH_ERROR_TEXT}
            </p>
          )}
          {term.ready && searchState === 'ready' && results.length === 0 && (
            <p className="px-1 py-3 text-[13px] leading-[1.5] text-stone-600">
              No encontramos pacientes que coincidan con «{term.query}». Prueba con otro nombre o con el teléfono.
            </p>
          )}
          {term.ready && searchState === 'ready' && results.length > 0 && (
            <div role="radiogroup" aria-label="Pacientes encontrados" className="flex flex-col gap-2">
              {results.map((p) => {
                const on = selected === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    disabled={busy}
                    onClick={() => setSelected(p.id)}
                    className={`flex w-full items-center gap-3 rounded-[14px] border px-3.5 py-3 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 ${
                      on ? 'border-blue-600 bg-blue-50' : 'border-stone-200 bg-white hover:bg-stone-50'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`flex h-5 w-5 flex-none items-center justify-center rounded-full border-2 bg-white ${
                        on ? 'border-blue-600' : 'border-stone-400'
                      }`}
                    >
                      <span className={`h-2.5 w-2.5 rounded-full ${on ? 'bg-blue-600' : 'bg-transparent'}`} />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="truncate text-sm font-semibold text-stone-900">{p.full_name}</span>
                      <span className="text-xs text-stone-600">
                        Última visita <span className="font-geist-mono">{visitLabel(p.last_visit_date)}</span>
                      </span>
                    </span>
                    <span className="flex-none font-geist-mono text-[13px] text-stone-800">{p.phone || '—'}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {error && (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 pt-1.5">
          <span className="text-[13px] text-stone-600">
            ¿No está en la lista?{' '}
            <button
              type="button"
              disabled={busy}
              onClick={onCreate}
              className="border-0 bg-transparent p-0 text-[13px] font-semibold text-blue-700 underline hover:text-blue-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
            >
              Crear paciente
            </button>
          </span>
          <div className="flex gap-2">
            <button type="button" disabled={busy} onClick={onClose} className={secondaryButton}>
              Cancelar
            </button>
            <button type="submit" disabled={selected === null || busy} className={primaryButton}>
              Vincular paciente
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

interface CreateDialogProps extends ConversationProps {
  onClose: () => void;
  onLinked: () => void;
  /** The file was created but the link failed: the caller says so and closes this dialog. */
  onLinkFailed: (name: string) => void;
}

/** "Crear paciente": a minimal file (name and phone) created and linked in one step. */
export const CreatePatientDialog = ({ conversation, onClose, onLinked, onLinkFailed }: CreateDialogProps) => {
  const { linkPatient } = useInbox();
  const ids = useId();
  const firstRef = useRef<HTMLInputElement>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState(() => localPhone(conversation.contact_wa_id));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || !canCreate(firstName, lastName)) return;
    setBusy(true);
    setError(null);
    const typedName = `${firstName.trim()} ${lastName.trim()}`;
    let created: Awaited<ReturnType<typeof createPatient>>;
    try {
      created = await createPatient({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim(),
        history_hypertension: false,
        history_diabetes: false,
      });
      if (!created.success) throw new Error('create failed');
    } catch {
      setError(CREATE_ERROR_TEXT);
      setBusy(false);
      return;
    }
    // From here the file exists: a failure must never run the creation again.
    const name = created.data.full_name || typedName;
    const id = createdPatientId(created.data.id);
    if (id === null) {
      onLinkFailed(name);
      return;
    }
    try {
      await linkPatient(conversation.id, id);
      onLinked();
    } catch {
      onLinkFailed(name);
    }
  };

  const titleId = `${ids}-t`;
  const descId = `${ids}-d`;
  const ready = canCreate(firstName, lastName);

  return (
    <Modal
      role="dialog"
      titleId={titleId}
      descriptionId={descId}
      maxWidth="max-w-[480px]"
      locked={busy}
      onClose={onClose}
      initialFocus={firstRef}
    >
      <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <h2 id={titleId} className="text-xl font-bold text-stone-900">
            Crear paciente
          </h2>
          <p id={descId} className="text-sm leading-[1.55] text-stone-600">
            Se crea la ficha y queda vinculada a esta conversación. Los demás datos los completas después en la ficha.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5">
            <span className="flex justify-between text-[13px] font-semibold text-stone-800">
              Nombre<span className="font-normal text-stone-600">obligatorio</span>
            </span>
            <input
              ref={firstRef}
              type="text"
              value={firstName}
              autoComplete="off"
              disabled={busy}
              required
              aria-required="true"
              placeholder="Pedro"
              onChange={(e) => setFirstName(e.target.value)}
              className={fieldClass}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="flex justify-between text-[13px] font-semibold text-stone-800">
              Apellido<span className="font-normal text-stone-600">obligatorio</span>
            </span>
            <input
              type="text"
              value={lastName}
              autoComplete="off"
              disabled={busy}
              required
              aria-required="true"
              placeholder="Chávez"
              onChange={(e) => setLastName(e.target.value)}
              className={fieldClass}
            />
          </label>
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold text-stone-800">Teléfono</span>
          <input
            type="tel"
            value={phone}
            disabled={busy}
            onChange={(e) => setPhone(e.target.value)}
            className={`${fieldClass} font-geist-mono`}
          />
          <span className="text-xs text-stone-600">
            Viene del número de WhatsApp.
            {conversation.contact_name !== '' && <> Perfil de WhatsApp: {conversation.contact_name}.</>}
          </span>
        </label>

        {error && (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1.5">
          <button type="button" disabled={busy} onClick={onClose} className={secondaryButton}>
            Cancelar
          </button>
          <button type="submit" disabled={!ready || busy} className={primaryButton}>
            Crear y vincular
          </button>
        </div>
      </form>
    </Modal>
  );
};

/** The file exists but the link failed: nothing to retry here, the person links it later. */
export const LinkFailedDialog = ({ name, onClose }: { name: string; onClose: () => void }) => {
  const ids = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const titleId = `${ids}-t`;
  const descId = `${ids}-d`;

  return (
    <Modal
      titleId={titleId}
      descriptionId={descId}
      maxWidth="max-w-[440px]"
      locked={false}
      onClose={onClose}
      initialFocus={closeRef}
    >
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-amber-100 text-amber-800">
          <WarningIcon />
        </span>
        <h2 id={titleId} className="text-xl font-bold text-stone-900">
          No se pudo vincular
        </h2>
      </div>
      <p id={descId} className="text-sm leading-[1.55] text-stone-600">
        La ficha de <strong className="text-stone-800">{name}</strong> sí se creó, pero no quedó vinculada a esta
        conversación. Puedes vincularla más tarde con «Vincular a un paciente»; no hace falta crear otra ficha.
      </p>
      <div className="flex justify-end gap-2 pt-1.5">
        <button ref={closeRef} type="button" onClick={onClose} className={primaryButton}>
          Intentar más tarde
        </button>
      </div>
    </Modal>
  );
};
